import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Check, Zap, ArrowRight, MapPin, AlertTriangle, Lightbulb, Rocket, Gift, Lock } from "lucide-react";
import { useSecureAuth } from "@/hooks/useSecureAuth";
import { AuthDialog } from "@/components/AuthDialog";
import { PropertyNextStep } from "@/components/PropertyNextStep";
import { clearPendingOrder, orderFormSearch } from "@/lib/pendingOrder";
import { pageScrollBehavior } from "@/lib/scroll";
import { hasHouseNumber } from "@/lib/addressValidation";
import { FunnelEvent, trackCtaClick, trackFunnel } from "@/lib/funnel";
import { CREDIT_PACKAGE_PRICING } from "@/lib/pricing";
import { useLocale, interpolate } from "@/i18n/context";
import { checkoutPath, dashboardPath } from "@/i18n/routes";

export const Pricing = () => {
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [selectedApartmentNumber, setSelectedApartmentNumber] = useState<string | null>(null);
  const [selectedPropertyType, setSelectedPropertyType] = useState<string | null>(null);
  const [addressValid, setAddressValid] = useState(true);
  const navigate = useNavigate();
  const { user } = useSecureAuth();
  const { t, locale } = useLocale();
  const formatPrice = (value: number) => new Intl.NumberFormat(locale === "en" ? "en-GB" : locale, { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(value);

  useEffect(() => {
    // Read selected address, apartment number, and property type from sessionStorage
    const readAddress = () => {
      if (typeof window !== 'undefined') {
        const address = sessionStorage.getItem('selectedAddress');
        const apartmentNumber = sessionStorage.getItem('selectedApartmentNumber');
        setSelectedAddress(address);
        setSelectedApartmentNumber(apartmentNumber);
        setSelectedPropertyType(sessionStorage.getItem("selectedPropertyType"));

        // Validate address
        if (address) {
          const isValid = hasHouseNumber(address);
          setAddressValid(isValid);
          sessionStorage.setItem('addressValid', isValid.toString());
        } else {
          setAddressValid(true);
          sessionStorage.removeItem('addressValid');
        }
      }
    };

    // Read on mount
    readAddress();

    // Listen for storage changes (when address is selected in Hero component)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'selectedAddress' || e.key === 'selectedApartmentNumber' || e.key === 'selectedPropertyType') {
        readAddress();
      }
    };

    // Listen for custom event (for same-window updates)
    const handleAddressUpdate = () => {
      readAddress();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('addressSelected', handleAddressUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('addressSelected', handleAddressUpdate);
    };
  }, []);

  const plans = CREDIT_PACKAGE_PRICING.map((pricing, index) => {
    // specialNote is present on only some plan tuple members; treat as optional.
    const copy = t.pricing.plans[index] as (typeof t.pricing.plans)[number] & {
      specialNote?: string;
    };
    return {
      ...pricing,
      ...copy,
      popular: index === 1,
    };
  });

  const selectedOrder = selectedAddress ? {
    address: selectedAddress,
    propertyType: selectedPropertyType || undefined,
    apartmentNumber: selectedApartmentNumber || undefined,
  } : null;
  const previewDestination = dashboardPath(locale) + (selectedOrder ? orderFormSearch(selectedOrder) : "");

  const handlePurchase = async (planId: string, source = "pricing") => {
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;

    trackFunnel(FunnelEvent.CheckoutStarted, {
      source,
      packageName: plan.name,
      credits: plan.credits,
      amountCents: plan.price,
      currency: "eur",
      hasAddress: Boolean(selectedAddress),
    });

    const params = new URLSearchParams({
      credits: plan.credits.toString(),
      amount: plan.price.toString(),
      packageName: plan.name,
    });

    if (selectedAddress) {
      params.set('address', selectedAddress);
    }

    if (selectedApartmentNumber) {
      params.set('apartmentNumber', selectedApartmentNumber);
    }

    navigate(`${checkoutPath(locale)}?${params.toString()}`);
  };

  const handleFreePreview = (source = "pricing") => {
    trackFunnel(FunnelEvent.FreePreviewSelected, {
      source,
      hasAddress: Boolean(selectedAddress),
    });

    if (user) {
      if (selectedAddress) clearPendingOrder(selectedAddress);
      navigate(previewDestination);
      return;
    }
    setAuthDialogOpen(true);
  };

  return (
    <section id="pricing" tabIndex={-1} className="outline-none pricing-section scroll-mt-28 py-16 sm:py-24 bg-white">
      <div className="container mx-auto px-4">
        <div id="property-next-step" role={selectedOrder && addressValid ? "region" : undefined} aria-labelledby={selectedOrder && addressValid ? "property-next-heading" : undefined} tabIndex={-1} className="scroll-mt-36 outline-none focus-visible:rounded-2xl focus-visible:ring-2 focus-visible:ring-ring">
          {selectedOrder && addressValid && <PropertyNextStep
            order={selectedOrder}
            signedIn={Boolean(user)}
            fullPrice={formatPrice(CREDIT_PACKAGE_PRICING[0].price / 100)}
            onEdit={() => {
              trackCtaClick("property_next_step_edit");
              const input = document.querySelector<HTMLInputElement>('#hero [role="combobox"]');
              input?.focus({ preventScroll: true });
              input?.scrollIntoView({ behavior: pageScrollBehavior(), block: "center" });
            }}
            onPreview={() => handleFreePreview("property_next_step")}
            onPurchase={() => handlePurchase(CREDIT_PACKAGE_PRICING[0].id, "property_next_step")}
          />}
        </div>
        <div className="text-center mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4 px-0 py-0 border-0 bg-transparent text-primary uppercase tracking-[.14em] text-xs">
            <Zap className="h-4 w-4 mr-2" />
            {t.pricing.badge}
          </Badge>
          <h2 className="section-title mb-5">
            {t.pricing.title}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t.pricing.subtitle}
            <span className="mt-2 block text-primary font-semibold">{t.pricing.subtitleHighlight}</span>
          </p>
        </div>

        {/* Reference frame, set BEFORE the prices.
            The review's point: nobody arrives thinking "79 € is 0,04 % of the
            purchase price" — they think "that is a lot for a PDF". A discount
            badge ("save 35 %") anchors on our own price and reinforces that
            frame; this anchors on what the alternative costs instead.

            The court-appraisal comparison is used because it is verifiable. The
            review also suggests anchoring on a typical negotiating reserve of
            8.000–15.000 €, which is a stronger frame but a number we have no
            defensible source for — and printing an unsourced figure would
            undercut the independence the whole product sells. */}
        <div className="mx-auto mb-10 max-w-3xl rounded-2xl border border-border/60 bg-card/60 p-5 sm:p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="text-center sm:border-r sm:border-border/60">
              <p className="text-sm text-muted-foreground">{t.pricing.anchorAppraisalLabel}</p>
              <p className="mt-1 text-2xl font-bold text-muted-foreground">
                {t.pricing.anchorAppraisalValue}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">{t.pricing.anchorOursLabel}</p>
              <p className="mt-1 text-2xl font-extrabold text-primary">
                {t.pricing.anchorOursValue}
              </p>
            </div>
          </div>
          <p className="mt-4 text-center text-sm leading-relaxed text-muted-foreground">
            {t.pricing.anchorNote}
          </p>
        </div>

        {/* Address validation warning */}
        {selectedAddress && !addressValid && (
          <Alert variant="destructive" className="mb-8 max-w-2xl mx-auto">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {interpolate(t.pricing.addressWarning, { address: selectedAddress })}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto items-stretch">
          {plans.map((plan, planIndex) => (
            <Card
              key={plan.id}
              className={`price-plan relative surface-card-hover flex flex-col ${
                plan.popular
                  ? "price-plan-featured border-primary bg-primary text-white shadow-none"
                  : "bg-card"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-4 z-10">
                  <Badge className="bg-[#DFE9BD] text-[#23432E] font-semibold text-xs px-3 py-1 shadow-none">
                    {t.pricing.popular}
                  </Badge>
                </div>
              )}


              <CardHeader className="text-left pb-5 pt-8">
                <h3 className="text-lg font-semibold mb-1">{plan.name}</h3>
                <p className="text-muted-foreground mb-4">{plan.subtitle}</p>
                <div>
                  {plan.originalPrice && (
                    <div className="text-sm text-muted-foreground line-through">
                      {formatPrice(plan.originalPrice / 100)}
                    </div>
                  )}
                  <span className="text-4xl font-semibold tracking-[-0.05em] text-primary">
                    {formatPrice(plan.price / 100)}
                  </span>
                  <div className="text-sm text-muted-foreground mt-1">
                    {formatPrice(plan.pricePerCredit)} {t.pricing.perReport}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex flex-col flex-1 px-6 pb-8 pt-0">
                {plan.specialNote && (
                  <div className="flex items-start gap-2 px-3 py-2 mb-6 bg-primary/5 rounded-lg">
                    {planIndex === 1 ? (
                      <Lightbulb className="h-4 w-4 text-primary flex-shrink-0" />
                    ) : (
                      <Rocket className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                    <span className="text-sm text-muted-foreground">{plan.specialNote}</span>
                  </div>
                )}

                <ul className="space-y-3 flex-1">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className="h-5 w-5 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="h-3 w-3 text-primary" />
                      </div>
                      <span className="text-sm leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full mt-8 font-bold transition-all duration-300 ${
                    plan.popular
                      ? "bg-[#E2ECC6] text-[#153E35] hover:bg-white hover:text-primary hover:shadow-none"
                      : ""
                  } ${selectedAddress ? "h-auto flex-col gap-1 py-4 text-base" : "h-14 text-lg"}`}
                  variant={plan.popular ? "default" : "outline"}
                  onClick={() => handlePurchase(plan.id)}
                >
                  <div className="flex items-center justify-center">
                    {plan.buttonText || t.pricing.buy}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                  {selectedAddress && (
                    <div className="flex items-center justify-center gap-1.5 text-xs font-normal opacity-90">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate max-w-[180px]">
                        {selectedAddress}
                        {selectedApartmentNumber && `, ${t.pricing.aptPrefix} ${selectedApartmentNumber}`}
                      </span>
                    </div>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* The free preview sits after the paid packages, not before them. It is
            still the top of the funnel — it delivers the cover + contents of a
            real report and unlocks with a credit — but leading the section with
            0 € framed every paid tier as the expensive option before anyone had
            read what one contains. */}
        <Card className="relative surface-card-hover mt-6 sm:mt-8 max-w-6xl mx-auto border-border bg-[#F6F8F1] shadow-none">
          <div className="absolute -top-3 left-4 z-10">
            <Badge className="bg-[#E0E9D8] text-primary font-semibold text-xs px-3 py-1 shadow-none">
              {t.pricing.freePlan.badge}
            </Badge>
          </div>
          <CardContent className="flex flex-col gap-5 p-6 pt-8 lg:flex-row lg:items-center lg:gap-8">
            <div className="lg:w-64 lg:flex-shrink-0">
              <h3 className="text-2xl font-bold">{t.pricing.freePlan.name}</h3>
              <p className="text-muted-foreground">{t.pricing.freePlan.subtitle}</p>
              <div className="mt-3">
                <span className="text-4xl font-black text-primary">
                  {t.pricing.freePlan.priceLabel}
                </span>
              </div>
              <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-500/10 px-3 py-1.5">
                <Gift className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="text-xs text-muted-foreground">
                  {t.pricing.freePlan.specialNote}
                </span>
              </div>
            </div>

            <div className="flex-1">
              <ul className="grid gap-2 sm:grid-cols-2">
                {t.pricing.freePlan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-2.5">
                    <div className="mt-0.5 h-5 w-5 flex-shrink-0 rounded-full bg-emerald-500/10 flex items-center justify-center">
                      <Check className="h-3 w-3 text-primary" />
                    </div>
                    <span className="text-sm leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2">
                <Lock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                <span className="text-xs leading-relaxed text-muted-foreground">
                  {t.pricing.freePlan.lockedNote}
                </span>
              </div>
            </div>

            <div className="lg:w-56 lg:flex-shrink-0">
              <Button
                className="w-full h-12 font-bold bg-primary hover:bg-primary-hover transition-all duration-300"
                onClick={() => handleFreePreview()}
              >
                {user ? t.pricing.freePlan.buttonTextLoggedIn : t.pricing.freePlan.buttonText}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 max-w-5xl mx-auto space-y-2 text-sm text-muted-foreground">
          <div className="flex items-center justify-center gap-2">
            <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
            <span>{t.pricing.includesGurs}</span>
          </div>
          <div className="flex items-start justify-center gap-2 text-center">
            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <span>{t.pricing.notOfficialAppraisal}</span>
          </div>
        </div>

        <div className="text-center mt-12 sm:mt-16 px-4">
          <div className="bg-card/50 backdrop-blur-sm rounded-2xl p-6 max-w-2xl mx-auto border border-border/50">
            <p className="text-sm sm:text-base text-muted-foreground mb-3">
              <span className="font-semibold text-primary">{t.pricing.securePayment}</span> {t.pricing.securePaymentDesc}
            </p>
            <div className="flex justify-center items-center gap-4 text-xs text-muted-foreground">
              <span>{t.pricing.allCards}</span>
              <span>{t.pricing.instantActivation}</span>
            </div>
          </div>
        </div>
      </div>

      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} defaultMode="register" redirectTo={previewDestination} />
    </section>
  );
};
